import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useWizard, HealthSupportAnswers } from '../../context/WizardContext';
import { useProgressTracking } from '../../hooks/useProgressTracking';

/**
 * Preview defaults matching BASIC_MODE_DEFAULTS in taxAlphaTransform.js.
 * These are displayed to the user for transparency — actual defaults are applied server-side.
 */
const PREVIEW_DEFAULTS = {
  // AIME/PIA bend points for SS calculation
  SS_WAGE_BASE_CAP: 184500,
  SS_BEND_POINT_1: 1286,
  SS_BEND_POINT_2: 7749,
  SS_PIA_FACTOR_1: 0.90,
  SS_PIA_FACTOR_2: 0.32,
  SS_PIA_FACTOR_3: 0.15,
  SS_MIN_INCOME: 10000,
  // Retirement age
  DEFAULT_RETIREMENT_AGE: 67,
  NEAR_RETIREMENT_START: 62,
  NEAR_RETIREMENT_BUFFER: 2,
  NEAR_RETIREMENT_CAP: 70,
  // Target income
  TARGET_INCOME_REPLACEMENT_RATE: 0.80,
  RETIRED_WITHDRAWAL_RATE: 0.04,
  // Qualified/non-qualified allocation lookup (mirrors server-side BASIC_MODE_DEFAULTS.assetAllocation)
  QUALIFIED_PCT: {
    UNDER_50:    { UNDER_1M: 85, T_1M_2M: 79, T_2M_3M: 64, T_3M_5M: 55, T_5M_10M: 34, T_10M_PLUS: 25 },
    AGE_50_59:   { UNDER_1M: 85, T_1M_2M: 72, T_2M_3M: 65, T_3M_5M: 53, T_5M_10M: 36, T_10M_PLUS: 26 },
    AGE_60_69:   { UNDER_1M: 85, T_1M_2M: 68, T_2M_3M: 60, T_3M_5M: 51, T_5M_10M: 36, T_10M_PLUS: 26 },
    AGE_70_PLUS: { UNDER_1M: 85, T_1M_2M: 48, T_2M_3M: 46, T_3M_5M: 38, T_5M_10M: 24, T_10M_PLUS: 18 },
  } as Record<string, Record<string, number>>,
  MAX_QUALIFIED: {
    UNDER_50: 2200000, AGE_50_59: 3200000, AGE_60_69: 3800000, AGE_70_PLUS: 3000000,
  } as Record<string, number>,
};

/** Preview-only AIME/PIA calculation mirroring server-side calculateSSFromAIME */
const calculatePreviewSS = (annualIncome: number): number => {
  if (annualIncome < PREVIEW_DEFAULTS.SS_MIN_INCOME) return 0;
  const cappedIncome = Math.min(annualIncome, PREVIEW_DEFAULTS.SS_WAGE_BASE_CAP);
  const aime = cappedIncome / 12;
  let monthlyPIA = 0;
  if (aime <= PREVIEW_DEFAULTS.SS_BEND_POINT_1) {
    monthlyPIA = aime * PREVIEW_DEFAULTS.SS_PIA_FACTOR_1;
  } else if (aime <= PREVIEW_DEFAULTS.SS_BEND_POINT_2) {
    monthlyPIA = PREVIEW_DEFAULTS.SS_BEND_POINT_1 * PREVIEW_DEFAULTS.SS_PIA_FACTOR_1
               + (aime - PREVIEW_DEFAULTS.SS_BEND_POINT_1) * PREVIEW_DEFAULTS.SS_PIA_FACTOR_2;
  } else {
    monthlyPIA = PREVIEW_DEFAULTS.SS_BEND_POINT_1 * PREVIEW_DEFAULTS.SS_PIA_FACTOR_1
               + (PREVIEW_DEFAULTS.SS_BEND_POINT_2 - PREVIEW_DEFAULTS.SS_BEND_POINT_1) * PREVIEW_DEFAULTS.SS_PIA_FACTOR_2
               + (aime - PREVIEW_DEFAULTS.SS_BEND_POINT_2) * PREVIEW_DEFAULTS.SS_PIA_FACTOR_3;
  }
  return Math.round(monthlyPIA * 12);
};

/** Preview-only qualified % lookup mirroring server-side calculateQualifiedAllocation */
const getPreviewQualifiedPct = (totalSavings: number, oldestAge: number): number => {
  const ageGroup = oldestAge < 50 ? 'UNDER_50' : oldestAge < 60 ? 'AGE_50_59' : oldestAge < 70 ? 'AGE_60_69' : 'AGE_70_PLUS';
  const tier = totalSavings < 1000000 ? 'UNDER_1M' : totalSavings <= 2000000 ? 'T_1M_2M' : totalSavings <= 3000000 ? 'T_2M_3M' : totalSavings <= 5000000 ? 'T_3M_5M' : totalSavings < 10000000 ? 'T_5M_10M' : 'T_10M_PLUS';
  const pct = PREVIEW_DEFAULTS.QUALIFIED_PCT[ageGroup][tier];
  // Check if the max cap would reduce the effective percentage
  const maxQ = PREVIEW_DEFAULTS.MAX_QUALIFIED[ageGroup];
  const qualifiedRaw = Math.round(totalSavings * (pct / 100));
  if (qualifiedRaw > maxQ) {
    return Math.round((maxQ / totalSavings) * 100);
  }
  return pct;
};

const BasicReviewStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  useProgressTracking({ stepName: 'Contact & Review', stepNumber: 4 });
  const { personal, employment, socialSecurity, savings } = wizardData;

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Calculate age for display (parse year directly to avoid UTC timezone issues)
  const calculateAge = (dob: string): number => {
    if (!dob) return 0;
    const currentYear = new Date().getFullYear();
    const birthYear = parseInt(dob.substring(0, 4), 10);
    if (isNaN(birthYear)) return 0;
    return currentYear - birthYear;
  };

  const client1Age = calculateAge(personal.client1.dateOfBirth);

  // Calculate estimated defaults for preview
  const estimatedRetirementAge = (() => {
    if (client1Age >= PREVIEW_DEFAULTS.DEFAULT_RETIREMENT_AGE) return client1Age;
    if (client1Age >= PREVIEW_DEFAULTS.NEAR_RETIREMENT_START) {
      return Math.min(client1Age + PREVIEW_DEFAULTS.NEAR_RETIREMENT_BUFFER, PREVIEW_DEFAULTS.NEAR_RETIREMENT_CAP);
    }
    return PREVIEW_DEFAULTS.DEFAULT_RETIREMENT_AGE;
  })();

  const hasProvidedSS = !!(socialSecurity.client1.currentAmount || socialSecurity.client1.projectedAmount);
  const estimatedSS = calculatePreviewSS(employment.client1.currentIncome);

  // Client 2 estimates
  const client2Age = personal.hasPartner ? calculateAge(personal.client2.dateOfBirth) : 0;
  const estimatedRetirementAge2 = (() => {
    if (!personal.hasPartner || !client2Age) return 0;
    if (client2Age >= PREVIEW_DEFAULTS.DEFAULT_RETIREMENT_AGE) return client2Age;
    if (client2Age >= PREVIEW_DEFAULTS.NEAR_RETIREMENT_START) {
      return Math.min(client2Age + PREVIEW_DEFAULTS.NEAR_RETIREMENT_BUFFER, PREVIEW_DEFAULTS.NEAR_RETIREMENT_CAP);
    }
    return PREVIEW_DEFAULTS.DEFAULT_RETIREMENT_AGE;
  })();
  const hasProvidedSS2 = !!(socialSecurity.client2?.currentAmount || socialSecurity.client2?.projectedAmount);
  const estimatedSS2 = personal.hasPartner ? calculatePreviewSS(employment.client2?.currentIncome || 0) : 0;

  const totalAssets = wizardData.totalAssets || 0;
  const savingsTotal =
    savings.taxDeferred.reduce((s, a) => s + a.balance, 0) +
    savings.taxFree.reduce((s, a) => s + a.balance, 0) +
    savings.taxable.reduce((s, a) => s + a.balance, 0);
  const displayTotal = totalAssets || savingsTotal;
  const hasBreakdown = savingsTotal > 0;

  // Validation
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) { setEmailError('Email is required'); return; }
    if (!re.test(email)) { setEmailError('Please enter a valid email'); return; }
    setEmailError('');
  };

  const validatePhone = (phone: string) => {
    const re = /^[\d\+\-\(\) ]{10,15}$/;
    if (!phone) { setPhoneError('Phone number is required'); return; }
    if (!re.test(phone)) { setPhoneError('Please enter a valid phone number'); return; }
    setPhoneError('');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateWizardData({
      personal: { ...personal, contact: { ...personal.contact, email: value } }
    });
    if (emailError) validateEmail(value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateWizardData({
      personal: { ...personal, contact: { ...personal.contact, phone: value } }
    });
    if (phoneError) validatePhone(value);
  };

  const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  // Health & Support (Limited Interview – HealthSpan) — only present on that flow
  const healthSupport = wizardData.healthSupport;
  const hasHealthAnswers = (a?: HealthSupportAnswers) => !!a && !!(
    a.healthyMeals || a.communityConnection || a.healthConditionsNone ||
    a.healthConditions?.length || a.relyOnForCare?.length || a.dependOnYou?.length ||
    a.threeAmCall?.name || a.threeAmCall?.relationship
  );

  const healthRow = (label: string, value?: string) => !value ? null : (
    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'right' }}>{value}</Typography>
    </Box>
  );

  const renderHealthRows = (a: HealthSupportAnswers) => (
    <>
      {healthRow('Healthy meals', a.healthyMeals)}
      {healthRow('Community connection', a.communityConnection)}
      {healthRow('Health conditions', a.healthConditionsNone ? 'None' : a.healthConditions?.join(', '))}
      {healthRow('Support network', a.relyOnForCare?.join(', '))}
      {healthRow('Provides care for', a.dependOnYou?.join(', '))}
      {healthRow('3 AM call', a.threeAmCall?.name || a.threeAmCall?.relationship)}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" textAlign="center">
        Review Your Information
      </Typography>

      {/* What you told us */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon fontSize="small" color="success" /> What You Told Us
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body2" fontWeight="medium">
              {personal.client1.firstName} {personal.client1.lastName}
              {client1Age > 0 && ` (age ${client1Age})`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Annual Income</Typography>
            <Typography variant="body2" fontWeight="medium">{formatCurrency(employment.client1.currentIncome)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Total Savings</Typography>
            <Typography variant="body2" fontWeight="medium">{formatCurrency(displayTotal)}</Typography>
          </Box>
          {hasBreakdown && (
            <Box sx={{ ml: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {savings.taxDeferred.map((a, i) => (
                <Box key={`td-${i}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Tax-Deferred</Typography>
                  <Typography variant="caption">{formatCurrency(a.balance)}</Typography>
                </Box>
              ))}
              {savings.taxFree.map((a, i) => (
                <Box key={`tf-${i}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Tax-Free / Roth</Typography>
                  <Typography variant="caption">{formatCurrency(a.balance)}</Typography>
                </Box>
              ))}
              {savings.taxable.map((a, i) => (
                <Box key={`tx-${i}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Taxable</Typography>
                  <Typography variant="caption">{formatCurrency(a.balance)}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {hasProvidedSS && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Social Security</Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(socialSecurity.client1.currentAmount || socialSecurity.client1.projectedAmount || 0)}/yr
              </Typography>
            </Box>
          )}
          {personal.hasPartner && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Partner</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {personal.client2.firstName} {personal.client2.lastName}
                  {personal.client2.dateOfBirth && ` (age ${calculateAge(personal.client2.dateOfBirth)})`}
                </Typography>
              </Box>
              {employment.client2.currentIncome > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Partner's Income</Typography>
                  <Typography variant="body2" fontWeight="medium">{formatCurrency(employment.client2.currentIncome)}</Typography>
                </Box>
              )}
            </>
          )}
          {employment.client1.hasPension && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Pension</Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(employment.client1.pensionBenefitAmount || 0)}/yr starting at age {employment.client1.pensionStartAge || 67}
              </Typography>
            </Box>
          )}
          {hasHealthAnswers(healthSupport?.primary) && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                {personal.hasPartner
                  ? (personal.client1.firstName
                      ? `${personal.client1.firstName.toUpperCase()}'S HEALTH & SUPPORT`
                      : 'YOUR HEALTH & SUPPORT')
                  : 'HEALTH & SUPPORT'}
              </Typography>
              {renderHealthRows(healthSupport!.primary!)}
            </>
          )}
          {personal.hasPartner && hasHealthAnswers(healthSupport?.partner) && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                {(personal.client2.firstName || 'PARTNER').toUpperCase()}&apos;S HEALTH & SUPPORT
              </Typography>
              {renderHealthRows(healthSupport!.partner!)}
            </>
          )}
        </Box>
      </Paper>

      {/* What we'll estimate */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', border: '1px solid rgba(25, 118, 210, 0.12)' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" color="info" /> What We've Estimated For You
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {personal.hasPartner && estimatedRetirementAge2 > 0
            ? <Chip label={`Retirement ages: ${personal.client1.firstName || 'Client 1'} at ${estimatedRetirementAge}, ${personal.client2.firstName || 'Client 2'} at ${estimatedRetirementAge2}`} size="small" variant="outlined" />
            : <Chip label={`Retirement age: ${estimatedRetirementAge}`} size="small" variant="outlined" />
          }
          {!hasProvidedSS && estimatedSS > 0 && (
            <Chip label={`${personal.hasPartner ? `${personal.client1.firstName || 'Client 1'} ` : ''}Social Security: ~${formatCurrency(estimatedSS)}/yr`} size="small" variant="outlined" />
          )}
          {personal.hasPartner && !hasProvidedSS2 && estimatedSS2 > 0 && (
            <Chip label={`${personal.client2.firstName || 'Client 2'} Social Security: ~${formatCurrency(estimatedSS2)}/yr`} size="small" variant="outlined" />
          )}
          {!hasBreakdown && displayTotal > 0 && (() => {
            const client2Age = personal.hasPartner ? calculateAge(personal.client2.dateOfBirth) : 0;
            const oldestAge = Math.max(client1Age, client2Age);
            const qPct = getPreviewQualifiedPct(displayTotal, oldestAge);
            return <Chip label={`Asset allocation: ${qPct}% qualified / ${100 - qPct}% non-qualified`} size="small" variant="outlined" />;
          })()}
          {(() => {
            // Mirrors estimateTargetIncome() server-side: the HIGHER of 80% of
            // income and 4% of savings — keep the two in step, this chip is a
            // preview of the number the plan will actually be built on.
            const combinedIncome = (employment.client1.currentIncome || 0) + (employment.client2?.currentIncome || 0);
            const fromIncome = Math.round(combinedIncome * PREVIEW_DEFAULTS.TARGET_INCOME_REPLACEMENT_RATE);
            const fromSavings = Math.round(displayTotal * PREVIEW_DEFAULTS.RETIRED_WITHDRAWAL_RATE);
            const useIncome = fromIncome >= fromSavings;
            const basis = useIncome ? '80% of income' : '4% of savings';
            return <Chip label={`Annual Cashflow (After-Tax): ~${formatCurrency(useIncome ? fromIncome : fromSavings)}/yr (${basis})`} size="small" variant="outlined" />;
          })()}
        </Box>
      </Paper>

      {/* Contact Information */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Contact Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your financial advisor will be in touch with the results.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Email"
            value={personal.contact.email || ''}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            onBlur={() => personal.contact.email && validateEmail(personal.contact.email)}
            required
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={personal.contact.phone || ''}
            onChange={handlePhoneChange}
            error={!!phoneError}
            helperText={phoneError}
            onBlur={() => personal.contact.phone && validatePhone(personal.contact.phone)}
            required
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default BasicReviewStep;
